import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { BookOpenText, Sparkles, Search, Loader2 } from "lucide-react";
import { lookupWord, askAi } from "../lib/api";
import { toast } from "sonner";

export default function DictionaryFab() {
    const [tab, setTab] = useState("dictionary");
    const [word, setWord] = useState("");
    const [entries, setEntries] = useState(null);
    const [dictLoading, setDictLoading] = useState(false);

    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [askLoading, setAskLoading] = useState(false);

    const doLookup = async (e) => {
        e?.preventDefault();
        if (!word.trim()) return;
        setDictLoading(true);
        setEntries(null);
        try {
            const res = await lookupWord(word.trim());
            setEntries(res.entries || []);
            if (!res.entries || res.entries.length === 0) {
                toast("No dictionary entry — try Ask AI for a definition.");
            }
        } catch (err) {
            toast.error("Lookup failed");
        } finally {
            setDictLoading(false);
        }
    };

    const doAsk = async (e) => {
        e?.preventDefault();
        if (!question.trim()) return;
        setAskLoading(true);
        setAnswer("");
        try {
            const res = await askAi({ question, provider: "gemini", model: "gemini-2.5-flash" });
            setAnswer(res.answer);
        } catch (err) {
            toast.error("AI is napping — try again.");
        } finally {
            setAskLoading(false);
        }
    };

    return (
        <Sheet>
            <SheetTrigger asChild>
                <button
                    data-testid="dictionary-fab"
                    className="fixed bottom-6 left-6 md:bottom-8 md:left-8 z-40 inline-flex items-center gap-2 bg-[#FDE047] text-black nb-border-4 px-5 py-3.5 rounded-2xl font-black uppercase tracking-widest text-xs nb-shadow hover:-translate-y-1 hover:shadow-[8px_8px_0px_#0A0A0A] active:translate-y-1 active:translate-x-1 active:shadow-none transition-all"
                >
                    <BookOpenText strokeWidth={3} className="w-4 h-4" />
                    Dictionary & Ask AI
                </button>
            </SheetTrigger>
            <SheetContent
                side="right"
                className="w-full sm:max-w-xl bg-[#FAFAFA] border-l-2 border-black overflow-y-auto"
            >
                <SheetHeader>
                    <SheetTitle className="font-display text-3xl font-black">
                        Look it up. Ask anything.
                    </SheetTitle>
                </SheetHeader>

                <div className="mt-4 flex gap-2 nb-border rounded-xl bg-white p-1">
                    <button
                        data-testid="tab-dictionary"
                        onClick={() => setTab("dictionary")}
                        className={`flex-1 py-2.5 font-bold text-sm rounded-lg uppercase tracking-widest ${tab === "dictionary" ? "bg-[#A7F3D0] nb-border" : ""}`}
                    >
                        Dictionary
                    </button>
                    <button
                        data-testid="tab-askai"
                        onClick={() => setTab("askai")}
                        className={`flex-1 py-2.5 font-bold text-sm rounded-lg uppercase tracking-widest ${tab === "askai" ? "bg-[#D8B4FE] nb-border" : ""}`}
                    >
                        Ask AI
                    </button>
                </div>

                {tab === "dictionary" && (
                    <div className="mt-5">
                        <form onSubmit={doLookup} className="flex gap-2">
                            <input
                                data-testid="dictionary-search-input"
                                value={word}
                                onChange={(e) => setWord(e.target.value)}
                                placeholder="Type a word (e.g. ephemeral)"
                                className="flex-1 nb-border rounded-xl px-4 py-3 font-bold bg-white focus:outline-none focus:nb-shadow"
                            />
                            <button
                                type="submit"
                                disabled={dictLoading}
                                className="bg-black text-white nb-border rounded-xl px-4 py-3 font-bold"
                            >
                                {dictLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Search className="w-4 h-4" strokeWidth={3} />
                                )}
                            </button>
                        </form>

                        <div className="mt-5 space-y-4" data-testid="dictionary-results">
                            {entries && entries.length === 0 && (
                                <div className="nb-border rounded-xl p-4 bg-white text-sm">
                                    No definition found. Try Ask AI instead.
                                </div>
                            )}
                            {entries && entries.map((e, i) => (
                                <div key={i} className="nb-border rounded-2xl p-4 bg-white nb-shadow-sm">
                                    <div className="flex items-baseline justify-between">
                                        <div className="font-display text-2xl font-black capitalize">{e.word}</div>
                                        {e.phonetic && (
                                            <span className="text-sm font-mono text-neutral-600">{e.phonetic}</span>
                                        )}
                                    </div>
                                    <div className="mt-3 space-y-3">
                                        {e.meanings.map((m, j) => (
                                            <div key={j}>
                                                <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-700 bg-[#FDE047] inline-block px-2 py-0.5 rounded-full nb-border">
                                                    {m.partOfSpeech}
                                                </div>
                                                <ol className="list-decimal ml-5 mt-1.5 text-sm space-y-1">
                                                    {m.definitions.map((d, k) => (
                                                        <li key={k}>{d}</li>
                                                    ))}
                                                </ol>
                                                {m.example && (
                                                    <div className="text-xs italic mt-1 text-neutral-600">
                                                        "{m.example}"
                                                    </div>
                                                )}
                                                {m.synonyms && m.synonyms.length > 0 && (
                                                    <div className="text-xs mt-1.5">
                                                        <span className="font-bold">Synonyms:</span>{" "}
                                                        {m.synonyms.join(", ")}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "askai" && (
                    <div className="mt-5">
                        <form onSubmit={doAsk} className="flex flex-col gap-3">
                            <textarea
                                data-testid="ask-ai-input"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                rows={4}
                                placeholder="Ask anything — explain quantum tunneling, best pasta tips, or fix my sentence."
                                className="w-full nb-border rounded-xl px-4 py-3 font-semibold bg-white focus:outline-none focus:nb-shadow resize-none"
                            />
                            <button
                                type="submit"
                                disabled={askLoading}
                                data-testid="ask-ai-submit"
                                className="self-start inline-flex items-center gap-2 bg-[#D8B4FE] text-black nb-border rounded-xl px-5 py-3 font-bold uppercase tracking-wider text-sm nb-shadow-sm hover:-translate-y-[2px] hover:shadow-[4px_4px_0px_#0A0A0A] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all"
                            >
                                {askLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Sparkles strokeWidth={3} className="w-4 h-4" />
                                )}
                                Ask AI
                            </button>
                        </form>
                        {answer && (
                            <div
                                data-testid="ask-ai-answer"
                                className="mt-4 nb-border rounded-2xl p-4 bg-white nb-shadow-sm whitespace-pre-wrap text-sm leading-relaxed"
                            >
                                {answer}
                            </div>
                        )}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
